import { ReactNode, useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useRouter } from "next/router";
import { SRPBannerType } from "@modules/vehicle/types/SRPBannerType";
import CloudflareImagesImage from "@common/components/CloudflareImagesImage";
import ActionButton from "@modules/cms/enum/ActionButtonEnum";
import getRelativeLink from "@modules/cms/helpers/getRelativeLink";
import CmsButtonModalContainer, {
  ButtonModalState,
} from "@modules/cms/components/ButtonModalContainer";
import { CmsButtonType } from "@modules/cms/types/CmsButton";
import { CarouselNavigation } from "@talladega/components";

type Props = {
  banner: SRPBannerType | null;
  isMobile: boolean;
};

const BannerButton = ({
  children,
  button,
  router,
  setButtonModalState,
}: {
  children: ReactNode;
  button: CmsButtonType;
  router: ReturnType<typeof useRouter>;
  setButtonModalState: React.Dispatch<React.SetStateAction<ButtonModalState>>;
}) => {
  const { asPath } = router;
  return (
    <button
      id="guba_ai_29"
      type="button"
      className="w-full"
      onClick={() => {
        if (button && !button.isHidden) {
          if (button.isButtonLink) {
            let linkUrl = button?.buttonLink?.url;

            if (button.persistQueryParams) {
              const queryParams = asPath.split("?")[1];
              linkUrl = queryParams
                ? `${button.buttonLink?.url}?${queryParams}`
                : button.buttonLink?.url;
            }
            router.push(getRelativeLink(linkUrl) ?? "");
          } else if (button.actionButton) {
            setButtonModalState({
              show: true,
              actionType: button.actionButton as unknown as ActionButton,
              formProps:
                button.actionButton === ActionButton.GravityForm
                  ? button.formData?.formSide
                  : undefined,
            });
          }
        }
      }}
    >
      {children}
    </button>
  );
};

const BannerImage = ({ src, alt }: { src: string; alt: string }) => (
  <CloudflareImagesImage
    src={src}
    alt={alt}
    width={0}
    height={0}
    sizes="100vw"
    style={{ width: "100%", height: "auto" }}
    className="rounded-lg"
  />
);

const SRPBanner = ({ banner, isMobile }: Props) => {
  const [emblaRef, embla] = useEmblaCarousel({
    align: "start",
    active:
      (banner &&
        banner.numberOfImages !== "Single" &&
        banner.imageGallery.length > 1) ??
      undefined,
    loop: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const router = useRouter();
  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  const scrollPrev = useCallback(() => {
    return embla && embla.scrollPrev();
  }, [embla]);
  const scrollNext = useCallback(() => {
    return embla && embla.scrollNext();
  }, [embla]);
  const scrollTo = useCallback(
    (index: number) => embla && embla.scrollTo(index),
    [embla]
  );

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);

  useEffect(() => {
    if (!embla) return undefined;
    const timer = setInterval(() => {
      if (embla.canScrollNext()) {
        embla.scrollNext();
      } else {
        embla.scrollTo(0);
      }
    }, 5000); // Change slides every 5 seconds

    return () => clearInterval(timer);
  }, [embla]);
  if (banner) {
    return (
      <div id="cms-srp-banner" className="relative">
        <div className="w-full overflow-hidden" ref={emblaRef}>
          <div className="flex select-none">
            {banner.numberOfImages === "Single" ? (
              <BannerButton
                button={banner.singleImage.button}
                router={router}
                setButtonModalState={setButtonModalState}
              >
                {isMobile ? (
                  <BannerImage
                    src={banner.singleImage.mobileImage.sourceUrl}
                    alt={banner.singleImage.mobileImage.altText}
                  />
                ) : (
                  <BannerImage
                    src={banner.singleImage.desktopImage.sourceUrl}
                    alt={banner.singleImage.desktopImage.altText}
                  />
                )}
              </BannerButton>
            ) : (
              banner.imageGallery.map((image, i) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${image.mobileImage.sourceUrl}-${i}`}
                  className="flex-none w-full"
                >
                  <BannerButton
                    button={image.button}
                    router={router}
                    setButtonModalState={setButtonModalState}
                  >
                    {isMobile ? (
                      <BannerImage
                        src={image.mobileImage.sourceUrl}
                        alt={image.mobileImage.altText}
                      />
                    ) : (
                      <BannerImage
                        src={image.desktopImage.sourceUrl}
                        alt={image.desktopImage.altText}
                      />
                    )}
                  </BannerButton>
                </div>
              ))
            )}
          </div>
        </div>
        {banner.numberOfImages !== "Single" &&
          banner.imageGallery.length > 1 && (
            <div className="absolute flex justify-center w-full -mt-9">
              <CarouselNavigation
                buttonColours="primary"
                contentList={banner.imageGallery}
                scrollNext={scrollNext}
                scrollPrev={scrollPrev}
                scrollTo={scrollTo}
                selectedIndex={selectedIndex}
                hiddenOnMobile={false}
                showArrows={false}
              />
            </div>
          )}
        {buttonModalState.show && (
          <CmsButtonModalContainer buttonModalState={buttonModalState} />
        )}
      </div>
    );
  }
  return null;
};

export default SRPBanner;
